import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CurrencyExchangeRoundedIcon from '@mui/icons-material/CurrencyExchangeRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import MonitorRoundedIcon from '@mui/icons-material/MonitorRounded';
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded';
import './ParametersDialog.css';


const getTheme = () => {
  const cookie = document.cookie.match(/theme=([^;]+)/);
  if (cookie) return cookie[1];
  return 'system';
};

const getCurrency = () => {
  const cookie = document.cookie.match(/currency=([^;]+)/);
  if (cookie) return cookie[1];
  return '';
};

const THEME_OPTIONS = [
  {
    value: 'light',
    label: 'Clair',
    description: 'Une interface nette et lumineuse, pensée pour la lecture rapide.',
    Icon: LightModeRoundedIcon,
  },
  {
    value: 'dark',
    label: 'Sombre',
    description: 'Un affichage plus dense visuellement pour les longues sessions.',
    Icon: DarkModeRoundedIcon,
  },
  {
    value: 'system',
    label: 'Système',
    description: 'Suit automatiquement les préférences de votre appareil.',
    Icon: MonitorRoundedIcon,
  },
];


function ParametersDialog({ open, onClose, onThemeChange, onCurrencyChange }) {
  const muiTheme = useMuiTheme();
  const fullScreen = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [theme, setTheme] = useState(getTheme());
  const [currency, setCurrency] = useState(getCurrency());
  const [currencies, setCurrencies] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [currencyError, setCurrencyError] = useState('');

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const controller = new AbortController();

    setTheme(getTheme());
    setCurrency(getCurrency());
    setLoadingCurrencies(true);
    setCurrencyError('');

    fetch('/api/currencies', { signal: controller.signal })
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        setCurrencies(Array.isArray(data) ? data : []);
      })
      .catch(error => {
        if (error.name === 'AbortError') {
          return;
        }
        setCurrencies([]);
        setCurrencyError('Impossible de charger la liste des devises pour le moment.');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoadingCurrencies(false);
        }
      });

    return () => controller.abort();
  }, [open]);

  const handleTheme = (value) => {
    setTheme(value);
    document.cookie = `theme=${value};path=/;max-age=31536000`;
    onThemeChange(value);
  };

  const handleCurrency = (e) => {
    const value = e.target.value;
    setCurrency(value);
    document.cookie = `currency=${value};path=/;max-age=31536000`;
    if (onCurrencyChange) onCurrencyChange(value);
  };

  const activeTheme = THEME_OPTIONS.find((option) => option.value === theme) || THEME_OPTIONS[2];

  const panelSx = {
    borderRadius: 4,
    border: `1px solid ${alpha('#7c6335', 0.16)}`,
    background: 'rgba(255,255,255,0.72)',
    boxShadow: '0 18px 40px rgba(25, 36, 46, 0.08)',
    backdropFilter: 'blur(16px)',
    p: { xs: 2, sm: 2.5 },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(15, 23, 32, 0.58)',
            backdropFilter: 'blur(8px)',
          },
        },
      }}
      PaperProps={{
        sx: {
          overflow: 'hidden',
          borderRadius: { xs: 0, sm: 4 },
          border: '1px solid rgba(123, 97, 48, 0.16)',
          background: 'linear-gradient(180deg, #fdf8ef 0%, #f5eee1 100%)',
          boxShadow: '0 32px 80px rgba(16, 26, 35, 0.28)',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at top left, rgba(214, 170, 83, 0.20), transparent 35%), radial-gradient(circle at top right, rgba(41, 121, 255, 0.10), transparent 26%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Box sx={{ position: 'relative', px: { xs: 2.25, sm: 3 }, pt: { xs: 2.25, sm: 3 }, pb: 2.25 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="overline"
                sx={{
                  letterSpacing: '0.18em',
                  fontWeight: 700,
                  color: '#8b6c2d',
                }}
              >
                Préférences
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#17323c', lineHeight: 1.1, mb: 0.75 }}>
                Personnaliser l’interface
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(23, 50, 60, 0.78)', maxWidth: 440 }}>
                Ajustez le thème et la devise d’affichage. Les changements sont appliqués immédiatement et mémorisés localement.
              </Typography>
            </Box>

            <IconButton
              onClick={onClose}
              aria-label="Fermer les paramètres"
              sx={{
                bgcolor: 'rgba(255,255,255,0.72)',
                border: '1px solid rgba(23, 50, 60, 0.08)',
                boxShadow: '0 10px 24px rgba(15, 23, 32, 0.08)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.94)',
                },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
            <Chip
              icon={<PaletteRoundedIcon />}
              label={`Thème : ${activeTheme.label}`}
              sx={{
                bgcolor: 'rgba(255,255,255,0.7)',
                border: '1px solid rgba(123, 97, 48, 0.14)',
                fontWeight: 700,
              }}
            />
            <Chip
              icon={<CurrencyExchangeRoundedIcon />}
              label={`Devise : ${currency || 'Défaut'}`}
              sx={{
                bgcolor: 'rgba(255,255,255,0.7)',
                border: '1px solid rgba(123, 97, 48, 0.14)',
                fontWeight: 700,
              }}
            />
          </Stack>
        </Box>

        <Stack spacing={2} sx={{ px: { xs: 2.25, sm: 3 }, pb: { xs: 2.25, sm: 3 } }}>
          <Box sx={panelSx}>
            <Stack spacing={0.75} sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#17323c' }}>
                Apparence
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(23, 50, 60, 0.72)' }}>
                Choisissez le mode d’affichage le plus confortable pour votre environnement de travail.
              </Typography>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              {THEME_OPTIONS.map((option) => {
                const selected = theme === option.value;
                const Icon = option.Icon;

                return (
                  <ButtonBase
                    key={option.value}
                    onClick={() => handleTheme(option.value)}
                    sx={{
                      flex: 1,
                      borderRadius: 3,
                      textAlign: 'left',
                      alignItems: 'stretch',
                      border: selected ? '1px solid rgba(26, 86, 219, 0.26)' : '1px solid rgba(23, 50, 60, 0.08)',
                      background: selected ? 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(232,241,255,0.96) 100%)' : 'rgba(255,255,255,0.68)',
                      boxShadow: selected ? '0 18px 34px rgba(28, 88, 205, 0.12)' : 'none',
                      p: 1.75,
                      transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        borderColor: selected ? 'rgba(26, 86, 219, 0.36)' : 'rgba(23, 50, 60, 0.16)',
                        boxShadow: '0 18px 34px rgba(18, 33, 45, 0.10)',
                      },
                    }}
                  >
                    <Stack spacing={1.5} sx={{ width: '100%' }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                        <Box
                          sx={{
                            width: 42,
                            height: 42,
                            borderRadius: '14px',
                            display: 'grid',
                            placeItems: 'center',
                            color: selected ? '#1d4ed8' : '#17323c',
                            background: selected ? alpha('#1d4ed8', 0.12) : 'rgba(23, 50, 60, 0.06)',
                          }}
                        >
                          <Icon fontSize="small" />
                        </Box>

                        {selected ? (
                          <Chip
                            label="Actif"
                            size="small"
                            sx={{
                              height: 24,
                              fontWeight: 700,
                              bgcolor: 'rgba(29, 78, 216, 0.10)',
                              color: '#1d4ed8',
                            }}
                          />
                        ) : null}
                      </Stack>

                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#17323c', mb: 0.5 }}>
                          {option.label}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(23, 50, 60, 0.72)', lineHeight: 1.45 }}>
                          {option.description}
                        </Typography>
                      </Box>
                    </Stack>
                  </ButtonBase>
                );
              })}
            </Stack>
          </Box>

          <Box sx={panelSx}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1}
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#17323c' }}>
                  Devise d’affichage
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(23, 50, 60, 0.72)' }}>
                  Utilisée pour les montants affichés dans l’interface et les tableaux récapitulatifs.
                </Typography>
              </Box>

              <Chip
                size="small"
                label={loadingCurrencies ? 'Chargement…' : currency || 'Défaut'}
                sx={{
                  fontWeight: 700,
                  bgcolor: 'rgba(255,255,255,0.76)',
                  border: '1px solid rgba(23, 50, 60, 0.10)',
                }}
              />
            </Stack>

            <Stack direction="row" spacing={1.25} alignItems="center">
              <FormControl fullWidth size="small">
                <InputLabel id="parameters-currency-label">Devise</InputLabel>
                <Select
                  labelId="parameters-currency-label"
                  value={currency}
                  label="Devise"
                  onChange={handleCurrency}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.88)',
                    borderRadius: 2.5,
                  }}
                >
                  <MenuItem value="">Devise par défaut</MenuItem>
                  {currencies.map((item) => (
                    <MenuItem key={item.code} value={item.code}>
                      {item.code} - {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {loadingCurrencies ? <CircularProgress size={20} sx={{ color: '#8b6c2d' }} /> : null}
            </Stack>

            {currencyError ? (
              <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2.5 }}>
                {currencyError}
              </Alert>
            ) : null}
          </Box>

          <Stack
            direction={{ xs: 'column-reverse', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={1.5}
            sx={{ px: 0.25, pt: 0.25 }}
          >
            <Typography variant="caption" sx={{ color: 'rgba(23, 50, 60, 0.65)' }}>
              Les préférences sont stockées dans des cookies locaux pendant un an.
            </Typography>

            <Button
              onClick={onClose}
              variant="contained"
              sx={{
                alignSelf: { xs: 'stretch', sm: 'center' },
                borderRadius: 999,
                px: 2.25,
                py: 1.1,
                fontWeight: 800,
                textTransform: 'none',
                background: 'linear-gradient(135deg, #17323c 0%, #2d5565 100%)',
                boxShadow: '0 16px 30px rgba(23, 50, 60, 0.18)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #12262d 0%, #254755 100%)',
                },
              }}
            >
              Fermer
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
}

export default ParametersDialog;
